import { money, monthLong } from "@/lib/format";

export const pct = (p) => (p == null ? "—" : `${p >= 0 ? "+" : ""}${p.toFixed(1)}%`);
const tone = (c) => (c > 0 ? "text-emerald-700" : c < 0 ? "text-rose-600" : "text-muted-foreground");

const Th = ({ children, left }) => (
  <th className={`px-3 sm:px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap ${left ? "text-left" : "text-right"}`}>{children}</th>
);
const Td = ({ children, left, className = "" }) => (
  <td className={`px-3 sm:px-4 py-2.5 ${left ? "text-left" : "text-right font-num whitespace-nowrap"} ${className}`}>{children}</td>
);

export const Card = ({ title, subtitle, children, testId, right }) => (
  <section className="surface overflow-hidden fade-up" data-testid={testId}>
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-5 pt-4 pb-3">
      <div>
        <h2 className="font-heading text-lg font-semibold">{title}</h2>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
    <div className="overflow-x-auto">{children}</div>
  </section>
);

const totalRow = "bg-amber-50 font-semibold border-t-2 border-foreground";

export function PnlTable({ pnl }) {
  const section = (title, lines, label, total, kind) => (
    <>
      <tr className="bg-secondary/60"><Td left className="font-semibold">{title}</Td><Td /></tr>
      {lines.map((l) => (
        <tr key={l.category_id} className="border-b border-border" data-testid={`pnl-${kind}-line-${l.category_id}`}>
          <Td left className="pl-6 sm:pl-8">{l.name}{l.is_archived ? " (archived)" : ""}</Td><Td>{money(l.total_cents)}</Td>
        </tr>
      ))}
      <tr className={totalRow}><Td left>{label}</Td><Td><span data-testid={`pnl-total-${kind}`}>{money(total)}</span></Td></tr>
    </>
  );
  return (
    <Card title="Profit & Loss statement" testId="report-pnl">
      <table className="w-full text-sm">
        <thead className="bg-foreground text-white"><tr><Th left>Line</Th><Th>Amount</Th></tr></thead>
        <tbody>
          {section("Income", pnl.income, "Total income", pnl.total_income_cents, "income")}
          {section("Expenses", pnl.expense, "Total expenses", pnl.total_expense_cents, "expense")}
          <tr className={totalRow}><Td left>Net income</Td>
            <Td className={pnl.net_cents >= 0 ? "text-emerald-700" : "text-rose-600"}><span data-testid="pnl-net-income">{money(pnl.net_cents)}</span></Td></tr>
          <tr><Td left className="text-muted-foreground">Vendor purchases (included in expenses)</Td>
            <Td className="text-muted-foreground"><span data-testid="pnl-vendor-purchases">{money(pnl.vendor_purchases_cents)}</span></Td></tr>
        </tbody>
      </table>
    </Card>
  );
}

export function MonthlyTable({ rows, totals }) {
  return (
    <Card title="Month by month" subtitle="Partial months are limited to the selected dates" testId="report-monthly">
      <table className="w-full text-sm min-w-[560px]">
        <thead className="bg-foreground text-white"><tr><Th left>Month</Th><Th>Income</Th><Th>Expenses</Th><Th>Vendor purchases</Th><Th>Net</Th></tr></thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.month} className="border-b border-border" data-testid={`report-month-row-${m.month}`}>
              <Td left>{monthLong(m.month)}</Td><Td className="text-emerald-700">{money(m.income_cents)}</Td>
              <Td className="text-rose-600">{money(m.expense_cents)}</Td><Td>{money(m.vendor_purchases_cents)}</Td>
              <Td className={tone(m.net_cents)}>{money(m.net_cents)}</Td>
            </tr>
          ))}
          <tr className={totalRow} data-testid="report-month-total-row">
            <Td left>Total</Td><Td>{money(totals.income_cents)}</Td><Td>{money(totals.expense_cents)}</Td>
            <Td>{money(totals.vendor_purchases_cents)}</Td><Td>{money(totals.net_cents)}</Td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
}

export function ComparisonTable({ lines, which, base }) {
  const title = which === "previous" ? "vs previous period" : "vs same period last year";
  return (
    <Card title={title} subtitle={`Compared with ${base.label}`} testId={`report-compare-${which}`}>
      <table className="w-full text-sm min-w-[600px]">
        <thead className="bg-foreground text-white">
          <tr><Th left>Line</Th><Th>This period</Th><Th>{base.label}</Th><Th>$ change</Th><Th>% change</Th></tr>
        </thead>
        <tbody>
          {lines.map((l) => {
            const strong = l.kind === "total" || l.kind === "net";
            return (
              <tr key={l.key} className={strong ? totalRow : "border-b border-border"} data-testid={`compare-${which}-row-${l.key}`}>
                <Td left>{l.label}</Td><Td>{money(l.current_cents)}</Td><Td>{money(l[`${which}_cents`])}</Td>
                <Td>{money(l[`${which}_change_cents`])}</Td>
                <Td className={l[`${which}_pct`] == null ? "text-muted-foreground" : ""}><span data-testid={`compare-${which}-pct-${l.key}`}>{pct(l[`${which}_pct`])}</span></Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

export function VendorTable({ rows, totals }) {
  return (
    <Card title="Vendor totals" subtitle="All expenses grouped by vendor/payee. Vendor purchases counts only the Vendor Purchases & Supplier Expenses category." testId="report-vendors">
      {rows.length === 0 ? <p className="px-5 pb-5 text-sm text-muted-foreground" data-testid="report-vendors-empty">No data for this period</p> : (
        <table className="w-full text-sm min-w-[520px]">
          <thead className="bg-foreground text-white"><tr><Th left>Vendor / payee</Th><Th>Payments</Th><Th>Vendor purchases</Th><Th>Total paid</Th></tr></thead>
          <tbody>
            {rows.map((v) => (
              <tr key={v.vendor} className="border-b border-border" data-testid={`report-vendor-row-${v.vendor.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <Td left>{v.vendor}</Td><Td>{v.count}</Td><Td>{money(v.vendor_purchases_cents)}</Td><Td className="text-rose-600">{money(v.total_cents)}</Td>
              </tr>
            ))}
            <tr className={totalRow}><Td left>Total</Td><Td>{rows.reduce((s, v) => s + v.count, 0)}</Td>
              <Td>{money(totals.vendor_purchases_cents)}</Td><Td>{money(totals.total_cents)}</Td></tr>
          </tbody>
        </table>
      )}
    </Card>
  );
}
